import React, { useEffect, useState } from "react";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { StepNumberCircle } from "./WorkflowSetupTabs";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { RequestHandlingInstructionDto } from "../../../common/service/server/dto/RequestHandlingInstructionDto";
import { useRequestInstructions } from "../../hooks/useOrganizationVendors";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { Slideout, Toggle } from "@truevault/aquilo";
import {
  EmailTemplateWithCustomTextInputField,
  TRUEVAULT_BADGE_ICON,
} from "./DeleteInstructionsPage";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import { BoltIcon, BoltSlashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { Form } from "react-final-form";
import { PageLoading } from "../../components";
import "./vendor-instructions.css";
import clsx from "clsx";
import { useSnackBar, ExplanatoryCard } from "@truevault/aquilo";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { Routes } from "../../root/routes";
import { useHistory } from "react-router";
import { alphabetically } from "../../surveys/util";
import { useIntegrationsSettings } from "../../components/superorg/settings/IntegrationsSettings";
import { PrivacyCenterIntegrationsDto } from "../../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { OrganizationPublicId } from "../../../common/service/server/Types";

export const AccessRequestInstructionsPage = () => {
  const history = useHistory();
  const snackbar = useSnackBar();

  const [organization, , refreshOrg] = usePrimaryOrganization();

  const [showFooter, setShowFooter] = useState(true);

  const [
    autoSendRequestEmailNotificationsForAccessRequestsValue,
    setAutoSendRequestEmailNotificationsForAccessRequestsValue,
  ] = useState(null);

  useEffect(() => {
    if (organization) {
      setAutoSendRequestEmailNotificationsForAccessRequestsValue(
        organization.autoSendRequestEmailNotificationsForAccessRequests,
      );
    }
  }, [organization]);

  const { result: consumerRequestClosedKnowSampleMessage, refresh: refreshConsumerMessage } =
    useDataRequest({
      queryKey: ["emailTemplate", organization?.id, "closed-know"],
      api: () => Api.organization.getOrganizationMessageSample(organization?.id, "closed-know"),
    });

  const showCustomTextInputField =
    consumerRequestClosedKnowSampleMessage?.templateHTML?.includes("[[[CUSTOM_TEXT]]]");

  if (!organization) {
    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="access-request"
      nextOnClick={async () => {
        history.push(Routes.organization.requestHandling.requestToLimit);
      }}
      nextDisabled={false}
      hideFooter={!showFooter}
    >
      <>
        <div className="tw-boundary">
          <div className="max-w-[924px] ml-[40px]">
            <ExplanatoryCard
              topBody={
                <div>
                  <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                    What is an Access Request?
                  </h3>
                  <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                    <p>
                      A consumer's request that your business send them a copy of the personal
                      information you are storing about them. To fulfill this request, your business
                      will download or copy over the personal information you have about the
                      requesting consumer.
                    </p>
                    <p>
                      Your access request workflow will be automatically configured based on your
                      inputs in the table below.
                    </p>
                  </div>
                </div>
              }
              expandable={false}
            />
          </div>

          <div className="flex items-center gap-3 mt-10">
            <StepNumberCircle text="1" />
            <div className="text-neutral-900 text-[18px] font-normal">
              Review your vendors with accessible data and their access instructions
            </div>
          </div>

          <div className="mt-1.5 ml-[40px]">
            <AccessRequestInstructionsVendorsTable
              orgId={organization.id}
              vendorsCollectionContexts={["CONSUMER"]}
              onSidebarChange={(isOpen: boolean) => {
                setShowFooter(!isOpen);
              }}
            />
          </div>

          <div className="flex items-center gap-3 mt-10">
            <StepNumberCircle text="2" />
            <div className="text-gray-900 text-[18px] font-normal">
              Auto-send request processing emails
            </div>
            <Toggle
              checked={autoSendRequestEmailNotificationsForAccessRequestsValue}
              onChange={async (checked) => {
                setAutoSendRequestEmailNotificationsForAccessRequestsValue(checked);
                await Api.organization.updateOrganization(organization.id, {
                  name: organization.name,
                  logoUrl: organization.logoUrl,
                  regulations: [],
                  autoSendRequestEmailNotificationsForAccessRequests: checked,
                });
                await refreshOrg();

                if (checked) {
                  snackbar("Enabled automatically sending access request email notifications", {
                    variant: "success",
                  });
                } else {
                  // closeSnackbar functionality not available in new API
                }
              }}
            />
          </div>
          <div className="text-neutral-900 font-normal text-[14px] max-w-[950px] ml-[40px] mt-[6px]">
            If this is toggled off, you will be prompted to manually click a button to send the
            vendor email and consumer notification email below for each request you process.
          </div>
        </div>

        {/* Put the email templates outside of tw-boundary since the template HTML will only render correctly with default styles, and will not render correctly if overriden with tailwind styles */}
        <div style={{ marginTop: "12px", marginLeft: "40px", marginBottom: "700px" }}>
          <div className="tw-boundary">
            <div className="text-neutral-900 text-[14px]">Consumer Email Notification Template</div>
          </div>

          <div
            style={{
              backgroundColor: "#F9F8FB",
              padding: "32px",
              maxWidth: "736px",
              marginTop: "6px",
            }}
          >
            <p>
              <strong>To:</strong> <span>consumer@example.com</span>
            </p>

            <p>
              <strong>Subject: A message about your request [REQUEST ID#]</strong>
            </p>

            {showCustomTextInputField ? (
              <EmailTemplateWithCustomTextInputField
                orgId={organization.id}
                consumerRequestSampleMessage={consumerRequestClosedKnowSampleMessage}
                refreshConsumerMessage={refreshConsumerMessage}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: consumerRequestClosedKnowSampleMessage?.templateHTML,
                }}
              ></div>
            )}
          </div>
        </div>
      </>
    </WorkflowSetupPageWrapper>
  );
};

export function AccessRequestInstructionsVendorsTable({
  orgId,
  vendorsCollectionContexts,
  onSidebarChange,
}: {
  orgId: OrganizationPublicId;
  vendorsCollectionContexts: CollectionContext[];
  onSidebarChange?: (isOpen: boolean) => void;
}) {
  const [allVendors, ,] = useOrganizationVendors(
    orgId,
    // All following args are default values from function signature, except for onlyUseSettled
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    vendorsCollectionContexts,
    /* onlyUseSettled = */ true,
  );
  let accessibleVendors = allVendors
    ?.filter((v) => v.dataAccessibility === "ACCESSIBLE")
    ?.sort(alphabetically);
  const [instructions, setInstructions] = useState<null | RequestHandlingInstructionDto[]>(null);
  const [, , fetchInstructions] = useRequestInstructions(
    orgId,
    "KNOW",
    (data: RequestHandlingInstructionDto[]) => {
      setInstructions(data);
    },
  );
  const { fetch: updateInstruction } = useActionRequest({
    api: async (newInstruction: RequestHandlingInstructionDto) => {
      await Api.requestHandlingInstructions.putInstruction(orgId, newInstruction);
    },
    onSuccess: fetchInstructions,
    // onError: () => {
    //   enqueueSnackbar(`Error updating vendor deletion settings`, { variant: "error" });
    // },
  });

  const { settings: apiIntegrations } = useIntegrationsSettings();

  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);

  if (!allVendors) {
    return null;
  }

  return (
    <div className="border border-solid border-gray-200 rounded-lg max-w-[736px] max-h-[636px] overflow-y-auto relative">
      <table>
        <colgroup>
          <col style={{ width: "338px" }} />
          <col style={{ width: "338px" }} />
          <col style={{ width: "60px" }} />
        </colgroup>
      </table>
      <thead className="sticky top-0 z-10">
        <tr>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tl-lg pl-6 text-left text-xs font-medium text-gray-500 uppercase"
          >
            Vendor
          </th>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-center text-xs font-medium text-gray-500 uppercase"
          >
            Access Instructions
          </th>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tr-lg text-center text-xs font-medium text-gray-500 uppercase"
          >
            &nbsp;
          </th>
        </tr>
      </thead>
      <tbody>
        {accessibleVendors?.map((dataRecipient) => {
          let knowInstruction = instructions?.find(
            (i) => i.vendorId == dataRecipient.vendorId && i.requestType == "KNOW",
          );

          return (
            <DataRecipientRowWithSidebar
              key={dataRecipient.id}
              organizationId={orgId}
              dataRecipient={dataRecipient}
              knowInstruction={knowInstruction}
              updateInstruction={updateInstruction}
              openSidebar={() => {
                setSidebarOpenForVendorId(dataRecipient.vendorId);
                if (onSidebarChange) {
                  onSidebarChange(true);
                }
              }}
              isSidebarOpen={sidebarOpenForVendorId === dataRecipient.vendorId}
              closeSidebar={() => {
                setSidebarOpenForVendorId(null);
                if (onSidebarChange) {
                  onSidebarChange(false);
                }
              }}
              apiIntegrations={apiIntegrations}
            />
          );
        })}
      </tbody>
    </div>
  );
}

function DataRecipientRowWithSidebar({
  organizationId,
  dataRecipient,
  knowInstruction,
  updateInstruction,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
  apiIntegrations,
}: {
  organizationId: string;
  dataRecipient: OrganizationDataRecipientDto;
  knowInstruction: RequestHandlingInstructionDto;
  updateInstruction: (instruction: RequestHandlingInstructionDto) => void;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  apiIntegrations: PrivacyCenterIntegrationsDto;
}) {
  const [userProvidedAccessInstruction, setUserProvidedAccessInstruction] = useState(
    knowInstruction?.processingInstructions ?? "",
  );

  const knowInstructionWithLocalChanges: RequestHandlingInstructionDto = knowInstruction
    ? {
        ...knowInstruction,
        processingInstructions: userProvidedAccessInstruction,
      }
    : {
        organizationId: organizationId,
        vendorId: dataRecipient.vendorId,
        requestType: "KNOW",
        processingInstructions: userProvidedAccessInstruction,
      };

  let hasVendorAccessInstructions = dataRecipient.accessInstructions?.length > 0;

  let vendorApiConnection = (apiIntegrations?.vendors || []).find(
    (apiIntegration) => apiIntegration.vendorId == dataRecipient.vendorId,
  );

  return (
    <>
      <tr
        key={dataRecipient.id}
        className={clsx("border-t border-solid border-gray-200", "cursor-pointer", {
          "bg-neutral-0": isSidebarOpen,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openSidebar();
        }}
      >
        <td className="px-6 py-3.5 w-[338px] max-w-[338px] border-b border-solid border-gray-200">
          <div className="flex items-center gap-3">
            <img src={dataRecipient.logoUrl} alt={dataRecipient.name} className="h-6 w-6" />
            <span className="text-[#111827] font-medium text-sm">{dataRecipient.name}</span>
            {vendorApiConnection &&
              (vendorApiConnection.connected ? (
                <BoltIcon className="h-4 w-4 text-green-600 bg-[#E2FDF3] p-0.5 rounded-3xl" />
              ) : (
                <BoltSlashIcon className="h-4 w-4 text-neutral-700 bg-[#F5F4F9] p-0.5 rounded-3xl" />
              ))}
          </div>
        </td>
        <td className="text-center w-[338px] max-w-[338px] break-all border-b border-solid border-gray-200">
          {accessInstructionsColumnValue(dataRecipient, knowInstructionWithLocalChanges)}
        </td>
        <td className="w-[60px] max-w-[60px] border-b border-solid border-gray-200">
          <ArrowRightCircleIcon
            className="w-[20px] h-[20px] text-primary-600 cursor-pointer"
            onClick={openSidebar}
          />
        </td>
      </tr>

      <Slideout
        open={isSidebarOpen}
        setOpen={(newIsOpen: boolean) => {
          if (newIsOpen) {
            openSidebar();
          } else {
            closeSidebar();
          }
        }}
        slideFrom="right"
      >
        <>
          <div className="bg-neutral-0 p-6">
            <div className="flex justify-between items-center">
              <h3 className="text-gray-900 text-[18px] font-bold">
                Access Request Vendor Settings
              </h3>
              <div className="cursor-pointer" onClick={closeSidebar}>
                <XMarkIcon className="w-6 h-6 text-[#9CA3AF]" />
              </div>
            </div>

            <div className="mt-1">
              <div className="flex items-center">
                <img src={dataRecipient.logoUrl} alt={dataRecipient.name} className="h-5 w-5" />
                <span className="text-gray-900 font-medium text-[18px] ml-3">
                  {dataRecipient.name}
                </span>
              </div>
              {dataRecipient.url?.length > 0 && (
                <div className="mt-1 text-[14px] font-normal text-gray-500">
                  {dataRecipient.url}
                </div>
              )}
            </div>
          </div>
          <div>
            <Form
              onSubmit={() => {}}
              render={() => {
                return (
                  <>
                    <div className="p-6">
                      <div className="flex justify-between items-center">
                        <div className="text-[14px] text-gray-700 font-medium">
                          Self Service Access Instructions
                        </div>
                        {hasVendorAccessInstructions && (
                          <div className="flex items-center gap-1">
                            {TRUEVAULT_BADGE_ICON}
                            <span className="text-primary-600 text-[10px] font-medium">
                              Verified
                            </span>
                          </div>
                        )}
                      </div>
                      {hasVendorAccessInstructions && (
                        <div className="vendor-instructions mt-3">
                          <div
                            dangerouslySetInnerHTML={{ __html: dataRecipient.accessInstructions }}
                          />
                        </div>
                      )}
                      <div className="mt-4">
                        <div className="text-neutral-700 text-[14px] font-medium">
                          {hasVendorAccessInstructions
                            ? "Add Your Access Instructions (optional)"
                            : "Add Your Access Instructions"}
                        </div>
                        <textarea
                          rows={4}
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-[14px] text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                          defaultValue={userProvidedAccessInstruction}
                          placeholder="E.g. find the consumer first at the settings.."
                          onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
                            let newUserProvidedKnowInstruction = e.target.value;

                            setUserProvidedAccessInstruction(newUserProvidedKnowInstruction);

                            let updatedInstruction = {
                              ...knowInstructionWithLocalChanges,
                              processingInstructions: newUserProvidedKnowInstruction,
                            };

                            await updateInstruction(updatedInstruction);
                          }}
                        />
                      </div>
                    </div>
                  </>
                );
              }}
            />
          </div>
        </>
      </Slideout>
    </>
  );
}

const accessInstructionsColumnValue = (
  vendor: OrganizationDataRecipientDto,
  knowInstruction: RequestHandlingInstructionDto,
): React.ReactNode => {
  let resultLabel: React.ReactNode = "",
    textColor = "",
    bgColor = "";

  if (vendor?.accessInstructions) {
    resultLabel = (
      <div className="flex items-center gap-1">
        {TRUEVAULT_BADGE_ICON}
        <span className="text-primary-600">Verified</span>
      </div>
    );
    bgColor = "bg-[#EDE9FE]";
  } else if (knowInstruction?.processingInstructions?.length > 0) {
    resultLabel = "Your Instructions";
    textColor = "text-[#1E40AF]";
    bgColor = "bg-[#DBEAFE]";
  } else {
    resultLabel = "Add Instructions";
    textColor = "text-[#92400E]";
    bgColor = "bg-[#FEF3C7]";
  }

  return (
    <div
      className={`inline-block px-2.5 py-0.5 font-medium text-sm rounded-full ${textColor} ${bgColor}`}
    >
      {resultLabel}
    </div>
  );
};
